import { lucideIcons, esc } from '../app.js';
import { BASE } from '../api.js';

export async function renderVerify(container) {
  container.innerHTML = `
    <div class="landing-header">
      <a class="landing-logo" href="#/drop" style="text-decoration:none;color:var(--text)">
        <svg viewBox="0 0 32 32" width="36" height="36" style="flex-shrink:0"><defs><linearGradient id="bk-verify" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-verify)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-verify)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-verify)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </a>
      <a class="landing-signin" href="#/login">Sign in</a>
    </div>
    <div style="max-width:640px;margin:0 auto;padding:2rem">
      <h2 style="margin-bottom:4px">Verify Certificate</h2>
      <p style="color:var(--text-secondary);font-size:13px;margin-bottom:24px">Verify a notarization certificate offline. Optionally provide the original document to check the hash.</p>

      <div class="card" style="margin-bottom:16px">
        <div class="card-header"><span class="card-title">Certificate JSON</span></div>
        <div id="cert-drop" style="border:2px dashed var(--border);border-radius:8px;padding:32px;text-align:center;cursor:pointer;transition:border-color .2s">
          <i data-lucide="file-json" style="width:32px;height:32px;color:var(--text-secondary);margin-bottom:8px"></i>
          <div class="drop-desktop-hint">Drop certificate JSON here or click to select</div>
          <label for="cert-input" class="btn btn-secondary btn-sm drop-mobile-btn" style="margin-top:8px"><i data-lucide="file-up" style="width:14px;height:14px"></i> Choose File</label>
          <input type="file" id="cert-input" accept=".json" style="display:none">
        </div>
        <div id="cert-name" style="margin-top:8px;font-size:13px;color:var(--text-secondary)"></div>
      </div>

      <div class="card" style="margin-bottom:16px">
        <div class="card-header"><span class="card-title">Original Document (optional)</span></div>
        <div id="doc-drop" style="border:2px dashed var(--border);border-radius:8px;padding:32px;text-align:center;cursor:pointer;transition:border-color .2s">
          <i data-lucide="file" style="width:32px;height:32px;color:var(--text-secondary);margin-bottom:8px"></i>
          <div class="drop-desktop-hint">Drop document here to verify hash</div>
          <label for="doc-input" class="btn btn-secondary btn-sm drop-mobile-btn" style="margin-top:8px"><i data-lucide="file-up" style="width:14px;height:14px"></i> Choose File</label>
          <input type="file" id="doc-input" style="display:none">
        </div>
        <div id="doc-name" style="margin-top:8px;font-size:13px;color:var(--text-secondary)"></div>
      </div>

      <button class="btn btn-primary" id="verify-btn" disabled>Verify</button>

      <div id="verify-results" style="margin-top:20px"></div>
    </div>
    <div class="landing-footer"><a href="#/architecture" class="arch-footer-link">End-to-end encrypted</a> &middot; <a href="#/architecture" class="arch-footer-link">Zero knowledge</a> &middot; <a href="#/privacy" class="arch-footer-link">No tracking</a> &middot; <a href="#/compare" class="arch-footer-link">Compare</a> &middot; <a href="#/faq" class="arch-footer-link">FAQ</a></div>
    <div class="eu-badge">\u{1F1EA}\u{1F1FA} Made &amp; operated in the EU</div>`;
  lucideIcons();

  let certData = null;
  let docBytes = null;

  const certDrop = document.getElementById('cert-drop');
  const certInput = document.getElementById('cert-input');
  const docDrop = document.getElementById('doc-drop');
  const docInput = document.getElementById('doc-input');
  const verifyBtn = document.getElementById('verify-btn');

  function setupDrop(dropEl, inputEl, nameEl, onFile) {
    dropEl.onclick = () => inputEl.click();
    dropEl.ondragover = (e) => { e.preventDefault(); dropEl.style.borderColor = 'var(--accent)'; };
    dropEl.ondragleave = () => { dropEl.style.borderColor = 'var(--border)'; };
    dropEl.ondrop = (e) => {
      e.preventDefault();
      dropEl.style.borderColor = 'var(--border)';
      if (e.dataTransfer.files.length) onFile(e.dataTransfer.files[0]);
    };
    inputEl.onchange = () => { if (inputEl.files.length) onFile(inputEl.files[0]); };
  }

  setupDrop(certDrop, certInput, 'cert-name', async (file) => {
    document.getElementById('cert-name').textContent = file.name;
    try {
      const text = await file.text();
      certData = JSON.parse(text);
      verifyBtn.disabled = false;
    } catch (e) {
      document.getElementById('cert-name').textContent = 'Invalid JSON: ' + e.message;
      certData = null;
      verifyBtn.disabled = true;
    }
  });

  setupDrop(docDrop, docInput, 'doc-name', async (file) => {
    document.getElementById('doc-name').textContent = file.name;
    const buf = await file.arrayBuffer();
    docBytes = new Uint8Array(buf);
  });

  verifyBtn.onclick = async () => {
    if (!certData) return;
    const results = document.getElementById('verify-results');
    results.innerHTML = '<p class="loading">Verifying...</p>';

    try {
      const checks = [];

      // 1. Document hash check (client-side via SubtleCrypto)
      if (docBytes) {
        const digest = await crypto.subtle.digest('SHA-256', docBytes);
        const hashHex = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
        const match = hashHex === certData.content_hash;
        checks.push({ name: 'Document Hash', pass: match, detail: match ? 'Hash matches certificate' : `Mismatch: ${hashHex.slice(0, 16)}... vs ${certData.content_hash.slice(0, 16)}...` });
      }

      // 2 & 3. Signature + Merkle verification via server-side /verify
      const verifyPayload = {
        version: certData.version,
        notarization_id: certData.notarization_id,
        content_hash: certData.content_hash,
        timestamp_millis: certData.timestamp_millis,
        tree_index: certData.tree_index,
        tree_size: certData.tree_size,
        tree_root: certData.tree_root,
        inclusion_proof: certData.inclusion_proof || [],
        signature: certData.signature,
        signing_key: certData.signing_key,
      };
      if (certData.blob_hash) verifyPayload.blob_hash = certData.blob_hash;
      const verifyRes = await fetch(`${BASE}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyPayload),
      });

      if (!verifyRes.ok) {
        const errText = await verifyRes.text();
        checks.push({ name: 'Server Verification', pass: false, detail: `Server error: ${errText}` });
      } else {
        const vr = await verifyRes.json();
        checks.push({ name: 'Ed25519 Signature', pass: vr.checks.signature, detail: vr.checks.signature ? 'Signature verified' : 'Signature invalid' });
        checks.push({ name: 'Merkle Inclusion', pass: vr.checks.inclusion, detail: vr.checks.inclusion ? 'Proof valid' : 'Proof invalid' });
        checks.push({ name: 'Signing Key', pass: vr.checks.key_matches, detail: vr.checks.key_matches ? 'Key matches server notary' : 'Unknown signing key' });
      }

      // 4. Blob hash (server-verified ciphertext)
      if (certData.blob_hash) {
        checks.push({ name: 'Blob Hash', pass: true, detail: 'Server-verified ciphertext hash included' });
      }

      // 5. Anchors
      if (certData.anchors && certData.anchors.length > 0) {
        checks.push({ name: 'RFC 3161 Anchors', pass: true, detail: `${certData.anchors.length} anchor(s) present` });
      }

      let html = '';
      for (const c of checks) {
        const icon = c.pass === true ? 'check-circle' : c.pass === false ? 'x-circle' : 'minus-circle';
        const color = c.pass === true ? 'var(--success)' : c.pass === false ? 'var(--danger)' : 'var(--text-secondary)';
        html += `
          <div style="display:flex;align-items:center;gap:8px;padding:10px 0;border-bottom:1px solid var(--border)">
            <i data-lucide="${icon}" style="width:20px;height:20px;color:${color};flex-shrink:0"></i>
            <div>
              <div style="font-weight:600">${esc(c.name)}</div>
              <div style="font-size:12px;color:var(--text-secondary)">${esc(c.detail)}</div>
            </div>
          </div>`;
      }

      // Metadata
      const eventType = certData.event_type === 'grant' ? 'Grant provenance' : 'Item';
      html += `
        <div style="margin-top:16px;font-size:12px;color:var(--text-secondary)">
          <div>Type: ${eventType}</div>
          <div>Timestamp: ${certData.timestamp || 'N/A'}</div>
          <div>Tree index: ${certData.tree_index ?? 'N/A'} / Tree size: ${certData.tree_size ?? 'N/A'}</div>
          <div>Notarization ID: ${certData.notarization_id || 'N/A'}</div>
        </div>`;

      results.innerHTML = html;
      lucideIcons();
    } catch (err) {
      results.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    }
  };
}

